import { useState, useEffect, useCallback } from 'react';

import {
    adminListFacilities,
    adminListChatGroup,
    adminAddChatGroupMember,
    adminRemoveChatGroupMember,
    searchUsers,
    getMe,
    type FacilityRow,
    type ChatGroupMember,
    type DirectorySearchResult,
} from '@/api/client';

interface Props {
    getToken: () => Promise<string>;
}

export default function ChatGroupsAdmin({ getToken }: Props) {
    const [facilities, setFacilities] = useState<FacilityRow[]>([]);
    const [selectedCode, setSelectedCode] = useState<string>('');
    const [members, setMembers] = useState<ChatGroupMember[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [query, setQuery] = useState('');
    const [results, setResults] = useState<DirectorySearchResult[] | null>(null);
    const [searching, setSearching] = useState(false);

    // Load facilities once
    useEffect(() => {
        getToken()
            .then(async t => {
                const [facs, me] = await Promise.all([adminListFacilities(t), getMe(t).catch(() => null)]);
                setFacilities(facs);
                if (facs.length) {
                    const codes = facs.map(f => f.FacilityCode);
                    const preferred = me?.defaultFacilityCode && codes.includes(me.defaultFacilityCode)
                        ? me.defaultFacilityCode
                        : facs[0].FacilityCode;
                    setSelectedCode(preferred);
                }
            })
            .catch(e => setError(e instanceof Error ? e.message : 'Failed to load facilities.'));
    }, [getToken]);

    const loadMembers = useCallback(async (code: string) => {
        if (!code) return;
        setLoading(true);
        setError(null);
        try {
            const token = await getToken();
            setMembers(await adminListChatGroup(token, code));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load group.');
        } finally {
            setLoading(false);
        }
    }, [getToken]);

    useEffect(() => { if (selectedCode) loadMembers(selectedCode); }, [selectedCode, loadMembers]);

    // Debounced directory search
    useEffect(() => {
        if (query.trim().length < 2) { setResults(null); setSearching(false); return; }
        setSearching(true);
        const h = setTimeout(async () => {
            try {
                const token = await getToken();
                const r = await searchUsers(token, query.trim());
                setResults(r.results);
            } catch (e) {
                setError(e instanceof Error ? e.message : 'Search failed.');
            } finally {
                setSearching(false);
            }
        }, 350);
        return () => clearTimeout(h);
    }, [query, getToken]);

    const addMember = async (u: DirectorySearchResult) => {
        try {
            const token = await getToken();
            await adminAddChatGroupMember(token, selectedCode, {
                entraObjectId: u.id, displayName: u.displayName, email: u.email,
            });
            setQuery(''); setResults(null);
            await loadMembers(selectedCode);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to add.');
        }
    };

    const removeMember = async (entraObjectId: string) => {
        try {
            const token = await getToken();
            await adminRemoveChatGroupMember(token, selectedCode, entraObjectId);
            await loadMembers(selectedCode);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to remove.');
        }
    };

    const memberIds = new Set(members.map(m => m.EntraObjectId));

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-sm font-semibold text-gray-800">Facility chat groups</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                    Schedulers in a facility's group see and answer carrier chats for that facility.
                </p>
            </div>

            {error && (
                <div className="text-xs rounded-md bg-red-50 text-red-700 px-3 py-2">{error}</div>
            )}

            <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Facility</label>
                <select
                    value={selectedCode}
                    onChange={e => setSelectedCode(e.target.value)}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                    {facilities.map(f => (
                        <option key={f.FacilityCode} value={f.FacilityCode}>
                            {f.DisplayName} ({f.FacilityCode})
                        </option>
                    ))}
                </select>
            </div>

            {/* Add scheduler */}
            <div className="relative">
                <input
                    type="text"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Search schedulers by name or email to add…"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
                {searching && <div className="text-xs text-gray-400 mt-1">Searching…</div>}
                {results && results.length > 0 && (
                    <div className="mt-1 border border-gray-200 rounded-md divide-y divide-gray-100 max-h-48 overflow-y-auto">
                        {results.map(u => {
                            const already = memberIds.has(u.id);
                            return (
                                <button
                                    key={u.id}
                                    disabled={already}
                                    onClick={() => addMember(u)}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 disabled:opacity-40 disabled:hover:bg-transparent flex justify-between items-center"
                                >
                                    <span>
                                        <span className="font-medium text-gray-800">{u.displayName}</span>
                                        <span className="text-gray-400 ml-2 text-xs">{u.email}</span>
                                    </span>
                                    <span className="text-xs text-blue-600">{already ? 'In group' : '+ Add'}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
                {results && results.length === 0 && !searching && (
                    <div className="text-xs text-gray-400 mt-1">No matches.</div>
                )}
            </div>

            {/* Current members */}
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Group members ({members.length})
                </div>
                {loading ? (
                    <div className="px-3 py-6 text-center text-sm text-gray-400">Loading…</div>
                ) : members.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-gray-400">
                        No schedulers in this facility's chat group yet.
                    </div>
                ) : (
                    members.map(m => (
                        <div key={m.EntraObjectId} className="px-3 py-2.5 flex justify-between items-center">
                            <div>
                                <div className="text-sm font-medium text-gray-800">{m.DisplayName ?? '(unnamed)'}</div>
                                <div className="text-xs text-gray-400">{m.Email}</div>
                            </div>
                            <button
                                onClick={() => removeMember(m.EntraObjectId)}
                                className="text-xs text-red-500 hover:text-red-700 hover:underline"
                            >
                                Remove
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
